import { destinationKey, destinationTitle, unreachableReason } from '@sentinel/web-core';
import type { Destination } from '@sentinel/shared';
import { Icon, type IconName } from '@sentinel/ui';
import { useTranslation } from 'react-i18next';
import { useContextSwitch } from './use-context-switch';

const ICON: Record<Destination['kind'], IconName> = {
  console: 'console',
  organization: 'building',
  support: 'shield',
};

/** «Dove lavori» in the user card (header proposal 11): organizations, platform console and
 *  support windows. One changes them rarely, so they left the topbar. Shown only to identities
 *  with more than one place to work. */
export function Destinations({ onLeave }: { onLeave: () => void }) {
  const { t } = useTranslation();
  const { items, current, enter } = useContextSwitch();
  if (items.length <= 1 || current === undefined) return null;

  const choose = (destination: Destination) => {
    if (destination.current) return;
    onLeave();
    enter.mutate(destination);
  };

  return (
    <div className="uc-sec" role="group" aria-labelledby="uc-where">
      <p className="mlabel" id="uc-where">
        {t('shell.context.label')}
      </p>
      <div className="uc-list">
        {items.map((destination) => {
          const blocked = unreachableReason(destination, t);
          return (
            <button
              key={destinationKey(destination)}
              type="button"
              className="uc-ent"
              aria-pressed={destination.current}
              disabled={blocked !== null || enter.isPending}
              onClick={() => choose(destination)}
            >
              <Icon name={ICON[destination.kind]} size={18} />
              <span>
                <b>{destinationTitle(destination, t)}</b>
                {blocked === null ? null : <span className="why">{blocked}</span>}
              </span>
              {destination.current ? (
                <Icon name="check" size={18} />
              ) : (
                <span className="go">{t('shell.context.go')}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
